/**
 * Shop — public page listing all ticketed events + their bundle add-ons.
 * Users can add tickets (+ selected bundle items) to the cart from here.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, ArrowUpRight, Calendar, Ticket, Package, ShoppingCart, ShieldCheck,
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { useCart } from '../context/CartContext';
import { CartButton } from '../components/CartDrawer';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

const EventTicketCard = ({ ev }) => {
  const { addItem } = useCart();
  const [selected, setSelected] = useState([]);

  const bundleTotal = (ev.bundle_items || [])
    .filter((_, i) => selected.includes(i))
    .reduce((s, b) => s + (b.price_cents || 0), 0);

  const totalCents = (ev.price_cents || 0) + bundleTotal;
  const capacity = ev.capacity;
  const sold = ev.tickets_sold || 0;
  const remaining = capacity != null ? Math.max(capacity - sold, 0) : null;
  const soldOut = capacity != null && remaining <= 0;

  const toggle = (idx) => {
    setSelected((prev) => prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]);
  };

  const add = () => {
    addItem({
      kind: 'event_ticket',
      ref_id: ev.id,
      name: ev.title,
      unit_amount_cents: totalCents,
      quantity: 1,
      bundle_indexes: selected,
      meta: {
        date_label: ev.date_label,
        selected_bundle_labels: (ev.bundle_items || [])
          .filter((_, i) => selected.includes(i))
          .map((b) => b.label),
      },
    });
    setSelected([]);
  };

  return (
    <div className="pro-card p-6 flex flex-col" data-testid={`shop-event-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-primary">
              {ev.date_label} · {ev.kind}
            </div>
            <h3 className="font-heading text-lg font-extrabold text-foreground tracking-tight leading-tight mt-0.5">
              {ev.title}
            </h3>
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl font-extrabold text-foreground tracking-tight">
            {money(ev.price_cents || 0)}
          </div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-foreground/50 font-bold">
            per ticket
          </div>
        </div>
      </div>
      {ev.body && <p className="mt-4 text-sm text-foreground/70 leading-relaxed">{ev.body}</p>}

      {capacity != null && (
        <div className="mt-4 flex items-center gap-2 text-xs">
          <div className="flex-1 h-1.5 rounded-full bg-primary/10 overflow-hidden">
            <div
              className="h-full bg-primary"
              style={{ width: `${Math.min(100, (sold / capacity) * 100)}%` }}
            />
          </div>
          <span className="text-foreground/60 font-bold">
            {soldOut ? 'Sold out' : `${remaining} left`}
          </span>
        </div>
      )}

      {(ev.bundle_items || []).length > 0 && (
        <div className="mt-5 pt-5 border-t border-border space-y-2.5">
          <div className="text-[10px] uppercase tracking-[0.14em] font-extrabold text-foreground/60">
            <Package className="w-3 h-3 inline-block mr-1" /> Add-ons
          </div>
          {(ev.bundle_items || []).map((b, idx) => (
            <label
              key={idx}
              className="flex items-center gap-3 py-1.5 cursor-pointer group"
              data-testid={`shop-bundle-${ev.id}-${idx}`}
            >
              <Checkbox
                checked={selected.includes(idx)}
                onCheckedChange={() => toggle(idx)}
                data-testid={`shop-bundle-check-${ev.id}-${idx}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-foreground">{b.label}</div>
                {b.description && <div className="text-xs text-foreground/60">{b.description}</div>}
              </div>
              <div className="text-sm font-extrabold text-foreground">
                {b.price_cents > 0 ? `+${money(b.price_cents)}` : 'Included'}
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <div className="text-sm text-foreground/60">Total <span className="font-extrabold text-foreground text-base">{money(totalCents)}</span></div>
        <Button
          onClick={add}
          disabled={soldOut}
          className="ml-auto rounded-full px-5 font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
          data-testid={`shop-add-to-cart-${ev.id}`}
        >
          {soldOut ? 'Sold out' : (<><ShoppingCart className="w-4 h-4 mr-1.5" /> Add to cart</>)}
        </Button>
      </div>
    </div>
  );
};

const Shop = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/shop/events`);
        setEvents(res.data?.items || []);
      } catch (_) {
        toast.error('Could not load shop');
      } finally {
        setLoading(false);
      }
    })();
  }, [API]);

  return (
    <div className="min-h-screen bg-background" data-testid="shop-page">
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark size="lg" subtitle="Learning · Tools · Community" onClick={() => navigate('/')} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-foreground/80">
            <button onClick={() => navigate('/platform')} className="hover:text-foreground transition">Platform</button>
            <button onClick={() => navigate('/pricing')} className="hover:text-foreground transition">Pricing</button>
            <button onClick={() => navigate('/why-us')} className="hover:text-foreground transition">Why us</button>
            <button className="text-foreground font-semibold">Shop</button>
          </nav>
          <div className="flex items-center gap-2">
            <CartButton />
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="font-semibold">Log in</Button>
          </div>
        </div>
      </header>

      <section className="bg-navy-spotlight text-background relative overflow-hidden">
        <div className="container mx-auto px-6 pt-12 pb-14 relative z-10">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1.5 text-xs text-background/60 hover:text-background transition mb-5"
            data-testid="shop-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to landing
          </button>
          <span className="pill-badge"><Ticket className="w-3.5 h-3.5 fill-primary-foreground" /> Tickets & bundles</span>
          <h1 className="font-heading mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-balance max-w-3xl">
            Reserve your seat at the next
            <br />
            <span className="text-amber-brand">FDAcademy event or workshop.</span>
          </h1>
          <p className="mt-4 text-background/70 max-w-2xl">
            Add tickets and optional bundle add-ons to your cart, then check out securely with Stripe. Free RSVP events live over on the <button onClick={() => navigate('/events')} className="underline hover:text-background" data-testid="shop-events-link">events calendar</button>.
          </p>
        </div>
      </section>

      <main className="container mx-auto px-6 py-14">
        {loading && <p className="text-foreground/60">Loading…</p>}
        {!loading && events.length === 0 && (
          <div className="pro-card p-10 text-center" data-testid="shop-empty">
            <Ticket className="w-8 h-8 text-foreground/30 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-extrabold text-foreground tracking-tight">No paid events on sale right now.</h2>
            <p className="mt-2 text-foreground/60">Check back soon — or browse the <button onClick={() => navigate('/events')} className="underline text-primary font-bold">free events calendar</button>.</p>
          </div>
        )}
        {!loading && events.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="shop-event-grid">
            {events.map((ev) => <EventTicketCard key={ev.id} ev={ev} />)}
          </div>
        )}

        <div className="mt-14 pro-card p-6 flex items-start gap-4" data-testid="shop-trust-strip">
          <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight">Secure checkout via Stripe.</h3>
            <p className="mt-1 text-sm text-foreground/70">Payments processed by Stripe. Your card details never touch our servers. Full refunds for cancellations 48h before the event.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Shop;
